import React, { useEffect, useRef, useState } from 'react';
import {
  Input,
  Form,
  Typography,
  Segmented,
  Tooltip,
  Col,
  Row,
} from 'antd';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import MapboxWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker';
import { useSelector } from 'react-redux';
import API from 'utils/api';
import { logger } from 'utils/debug';
import styled from 'styled-components';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

mapboxgl.workerClass = MapboxWorker;
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const PROD = process.env.REACT_APP_ENV === 'PRODUCTION';
const DEV = process.env.NODE_ENV === 'development';

const Mapa = ({
  form,
  interactive,
  showAddress = true,
  currentStep,
  height,
  disabled,
  withLatLngInputs,
  coordsList,
  zoom,
  allowSearch,
  maxBounds,
  connectMarkers,
  center: _center,
  useVisibleToggler,
}) => {
  const formInstance = Form.useFormInstance();
  const mapContainer = useRef();
  const theme = useSelector(({ app }) => app.theme);
  const [fullAddress, setFullAddress] = useState();
  const [map, setMap] = useState();
  const [visibleMap, setVisibleMap] = useState(!useVisibleToggler);
  const longitud = Form.useWatch('longitud', formInstance);
  const latitud = Form.useWatch('latitud', formInstance);

  const getFullAddress = async (lat, lon) => {
    try {
      if (!mapboxgl.accessToken) return;
      const baseURL = 'https://api.mapbox.com/geocoding/v5/mapbox.places/';
      const URL = `${baseURL}${lat},${lon}.json`;
      const res = await API.get(URL, { params: { access_token: mapboxgl.accessToken } });
      setFullAddress(res.data?.features[0]?.place_name);
    } catch (err) {
      logger(err);
    }
  };

  const resizeMap = () => {
    setTimeout(() => {
      if (map) map.resize();
    });
  };

  const findCenter = (markers) => {
    const lats = markers.map((m) => m.latitud);
    const lngs = markers.map((m) => m.longitud);
    return [
      (Math.min(...lngs) + Math.max(...lngs)) / 2,
      (Math.min(...lats) + Math.max(...lats)) / 2,
    ];
  };

  useEffect(() => () => map?.remove(), []);

  useEffect(() => {
    if (!visibleMap) return;
    document.querySelectorAll('#map .mapboxgl-marker').forEach((m) => m.remove());
    let center = _center || (longitud && latitud && [longitud, latitud]) || undefined;
    if (!center && coordsList.length) {
      center = findCenter(coordsList);
    }
    const _map = new mapboxgl.Map({
      container: mapContainer.current,
      style: `mapbox://styles/mapbox/${theme.name
        .toLowerCase() === 'dark' ? 'dark-v9' : 'streets-v11'}`,
      center,
      zoom: zoom || (longitud ? 12 : 9),
      interactive,
      maxBounds,
    });
    _map.on('load', () => {
      _map.resize();
      document.querySelectorAll('#map .mapboxgl-marker').forEach((m) => m.remove());
      if (coordsList.length) {
        coordsList.forEach((coord, idx) => {
          const el = document.createElement('div');
          el.style.fontWeight = 'bold';
          el.style.fontSize = 12;
          if (coord.innerText) {
            el.innerText = coord.innerText ?? '';
          } else if (coord.innerHTML) {
            el.innerHTML = coord.innerHTML ?? '';
          }
          el.className = `${coord.className} marker-item`;
          el.style = coord.style;
          const marker = new mapboxgl.Marker(el)
            .setLngLat([coord.longitud, coord.latitud]);
          marker.addTo(_map);
          if (coord.popup) {
            const popup = new mapboxgl.Popup({
              offset: 25,
              closeButton: null,
              maxWidth: 350,
              // closeOnClick: !/last/.test(coord.className),
            }).setHTML(coord.popup);
            marker.setPopup(popup);
            if (/last/.test(coord.className)) marker.togglePopup();
          }
          if (connectMarkers && idx < coordsList.length - 1) {
            const nextCoord = coordsList[idx + 1];
            _map.addSource(`route-${idx}`, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: [
                    [coord.longitud, coord.latitud],
                    [nextCoord.longitud, nextCoord.latitud],
                  ],
                },
              },
            });
            _map.addLayer({
              id: `route-${idx}`,
              source: `route-${idx}`,
              type: 'line',
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': '#080',
                'line-width': 8,
              },
            });
          }
        });
      }
      if (longitud && latitud) {
        const mrkr = new mapboxgl.Marker({ draggable: interactive, color: 'red' })
          .setLngLat([longitud, latitud])
          .addTo(_map);
        if (interactive) {
          mrkr.on('dragend', (evt) => {
            form.setFieldsValue({
              longitud: evt.target._lngLat.lng,
              latitud: evt.target._lngLat.lat,
            });
          });
        }
      }
    });
    if (interactive && allowSearch) {
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
      });
      geocoder.on('result', (e) => {
        const [lon, lat] = e.result.center;
        form.setFieldsValue({ longitud: lon, latitud: lat });
        document.querySelectorAll('#map .mapboxgl-marker').forEach((m) => m.remove());
        const mrkr = new mapboxgl.Marker({ draggable: interactive, color: 'yellow' })
          .setLngLat(e.result.center)
          .addTo(_map);
        mrkr.on('dragend', (evt) => {
          form.setFieldsValue({
            longitud: evt.target._lngLat.lng,
            latitud: evt.target._lngLat.lat,
          });
        });
      });
      _map.addControl(geocoder);
    }
    if (!longitud) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((e) => {
          _map.resize();
          const _coords = [e.coords.longitude, e.coords.latitude];
          form.setFieldsValue({
            longitud: e.coords.longitude,
            latitud: e.coords.latitude,
          });
          _map.jumpTo({ center: _coords });
          document.querySelectorAll('#map .mapboxgl-marker').forEach((m) => m.remove());
          const mrkr = new mapboxgl.Marker({ draggable: interactive, color: 'purple' })
            .setLngLat(_coords)
            .addTo(_map);
          mrkr.on('dragend', (evt) => {
            form.setFieldsValue({
              longitud: evt.target._lngLat.lng,
              latitud: evt.target._lngLat.lat,
            });
          });
        });
      }
    } else if (showAddress && longitud && latitud) {
      getFullAddress(longitud, latitud);
    }
    setMap(_map);
    setTimeout(resizeMap);
  }, [visibleMap]);

  useEffect(resizeMap, [currentStep]);

  const getDisabledReason = () => {
    if (disabled && !(longitud && latitud)) return '';
    if (!mapboxgl.accessToken) return 'Missing token';
    if (!PROD) return 'Solo disponible en prod';
    return null;
  };

  const disabledReason = getDisabledReason();

  return (
    <Container style={{ width: '100%' }}>
      {(showAddress && fullAddress) && (
        <div>
          <br />
          <Typography.Text>
            {`Ubicación: ${fullAddress}`}
          </Typography.Text>
        </div>
      )}
      {useVisibleToggler && (!disabled || (longitud && latitud)) && (
        <Segmented
          size="large"
          value={visibleMap}
          onChange={setVisibleMap}
          options={[
            {
              label: 'Capturar Geolocalización',
              value: false,
            },
            {
              label: disabledReason ? <Tooltip title={disabledReason}>Ubicar en mapa</Tooltip> : 'Ubicar en Mapa',
              value: true,
              disabled: DEV ? false : !!disabledReason,
            },
          ]}
          style={{ marginBottom: 10 }}
        />
      )}
      <div id="map" style={{ height: visibleMap ? height : 0 }} ref={mapContainer} />
      {withLatLngInputs && (
        <Row gutter={10}>
          <Col xs={{ order: 1, span: 24 }} sm={{ order: 1, span: 24 }} md={{ order: 1, span: 12 }}>
            <Form.Item label="Longitud" name="longitud">
              <Input disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={{ order: 3, span: 24 }} sm={{ order: 3, span: 24 }} md={{ order: 2, span: 12 }}>
            <Form.Item label="Coordenada UTM X" name="coordenada_utm_x">
              <Input disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={{ order: 2, span: 24 }} sm={{ order: 2, span: 24 }} md={{ order: 3, span: 12 }}>
            <Form.Item label="Latitud" name="latitud">
              <Input disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={{ order: 4, span: 24 }} sm={{ order: 4, span: 24 }} md={{ order: 4, span: 12 }}>
            <Form.Item label="Coordenada UTM Y" name="coordenada_utm_y">
              <Input disabled={disabled} />
            </Form.Item>
          </Col>
        </Row>
      )}
    </Container>
  );
};

const Container = styled.div`
  .marker-item {
    background-color: #4285F4;
    border-radius: 50%;
    color: #FFFFFF;
    font-size: 30px;
    width: 50px;
    height: 50px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    &.first {
      background-color: #ff4d4f;
    }
    &.last {
      background-color: #28a745;
    }
  }

  .marker-item::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 89%;
    transform: translate(-50%, 0);
    width: 0;
    height: 0;
    border-left: 16px solid transparent;
    border-right: 16px solid transparent;
    border-top: 16px solid #4285F4;
  }
  .marker-item.first::after {
    border-top: 16px solid #ff4d4f;
  }
  .marker-item.last::after {
    border-top: 16px solid #28a745;
  }
`;

Mapa.propTypes = {
  form: PropTypes.shape(),
  interactive: PropTypes.bool,
  showAddress: PropTypes.bool,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  withLatLngInputs: PropTypes.bool,
  coordsList: PropTypes.arrayOf(PropTypes.shape()),
  zoom: PropTypes.number,
  allowSearch: PropTypes.bool,
  connectMarkers: PropTypes.bool,
  maxBounds: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  center: PropTypes.arrayOf(PropTypes.number),
  useVisibleToggler: PropTypes.bool,
};

Mapa.defaultProps = {
  form: null,
  interactive: true,
  showAddress: false,
  currentStep: null,
  height: 350,
  disabled: false,
  withLatLngInputs: true,
  coordsList: [],
  zoom: null,
  allowSearch: true,
  connectMarkers: false,
  maxBounds: null,
  center: null,
  useVisibleToggler: true,
};

export default Mapa;
